import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { CometChatHome } from "../CometChat/components/CometChatHome/CometChatHome";
import { AppContextProvider } from "../CometChat/context/AppContext";
import { CometChatProvider } from "../CometChat/context/CometChatContext";
import MainLayout from "../layouts/MainLayout";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { getTrustedConnections } from "../services/network.services";
import useDraftMessages from "../hooks/useDraftMessages";
import useDraftSync from "../hooks/useDraftSync";
import { trackClickEvent } from "../mixpanel";
import "../CometChat/styles/MessagesMobile.css";
import { useAuth } from "../contexts/Auth";
import { useNavigate } from "react-router-dom";
import paths from "../constants/paths";

const Messages = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [trustedUserIds, setTrustedUserIds] = useState(undefined);
  const [currentTab, setCurrentTab] = useState("chats");
  const messagesContainerRef = useRef(null);
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();


  // Use draft messages hook
  useDraftMessages(messagesContainerRef);

  // Use draft sync hook for cross-component synchronization
  useDraftSync();

  useEffect(() => {
    trackClickEvent('Chat Interface Opened', { source: 'Messages Page' });
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      // Check if we are in the middle of a logout
      const isLoggingOut = sessionStorage.getItem("isLoggingOut");
      if (isLoggingOut) {
        // Remove the flag and skip setting redirectPath
        sessionStorage.removeItem("isLoggingOut");
      } else {
        const alreadySet = sessionStorage.getItem("redirectPath");
        if (!alreadySet) {
          const currentPath = window.location.pathname + window.location.search;
          sessionStorage.setItem("redirectPath", currentPath);
        }
      }
      navigate(paths.login);
    }
  }, [isLoggedIn]);

  // Create conversation request builder that only shows user conversations
  const conversationsRequestBuilder = useMemo(() => {
    if (activeFilter === "trusted_network") {
      return new CometChat.ConversationsRequestBuilder()
        .setLimit(30)
        .withBlockedInfo(true)
        .setConversationType("user")
        .setTags(["Trusted"]);
    } else {
      return new CometChat.ConversationsRequestBuilder()
        .setLimit(30)
        .withBlockedInfo(true)
        .setConversationType("user");
    }
  }, [activeFilter]);

  const handleFilterChange = useCallback((filter) => {
    setActiveFilter(filter);
  }, []);

  const handleTabChange = useCallback((tabId) => {
    setCurrentTab(tabId);
    // Reset filter when switching away from chats tab
    if (tabId !== "chats") {
      setActiveFilter("all");
      setTrustedUserIds(undefined);
    }
  }, []);

  return (
    <>
      <MainLayout
        currentPage="Messages"
        currentPageTitle="Messages"
        noFooter={true}
        noFeedback={true}
      >
        <Container>
          {/* Filter Chips - Only show on chats tab */}
          {currentTab === "chats" && (
            <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
              <Chip
                label="All"
                onClick={() => handleFilterChange("all")}
                sx={{
                  backgroundColor:
                    activeFilter === "all" ? "#F1832C" : "transparent",
                  color: activeFilter === "all" ? "white" : "#F1832C",
                  border: "1px solid #F1832C",
                  "&:hover": {
                    backgroundColor:
                      activeFilter === "all"
                        ? "#D87425"
                        : "rgba(241, 131, 44, 0.1)",
                  },
                }}
              />
              <Chip
                label="Trusted Network"
                onClick={() => handleFilterChange("trusted_network")}
                sx={{
                  backgroundColor:
                    activeFilter === "trusted_network"
                      ? "#F1832C"
                      : "transparent",
                  color:
                    activeFilter === "trusted_network" ? "white" : "#F1832C",
                  border: "1px solid #F1832C",
                  "&:hover": {
                    backgroundColor:
                      activeFilter === "trusted_network"
                        ? "#D87425"
                        : "rgba(241, 131, 44, 0.1)",
                  },
                }}
              />
            </Box>
          )}

          <div
            ref={messagesContainerRef}
            style={{
              height: "calc(100vh - 300px)",
              backgroundColor: "#fff",
              borderRadius: "8px",
              overflow: "visible",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <CometChatProvider
              authBuilderSetting={{
                layout: {
                  withSideBar: true,
                  tabs: ['chats', 'users'],
                  chatType: 'user'
                }
              }}
            >
              <AppContextProvider>
                <CometChatHome
                  conversationsRequestBuilder={conversationsRequestBuilder}
                  filterKey={activeFilter}
                  onTabChange={handleTabChange}
                />
              </AppContextProvider>
            </CometChatProvider>
          </div>
        </Container>
      </MainLayout>
    </>
  );
};

export default Messages;
